// @ts-strict-ignore
import React, { useRef } from 'react';
import { eventWithTime } from '@rrweb/types';
import clsx from 'clsx';
import { SvgIconReact } from 'common/components/svg-icon';
import { TooltipReact } from 'common/components/tooltip';
import { ProjectFeatureFlag } from 'common/report/constants';
import { useCurrentProject } from 'common/contexts/react/current-project-context';
import { RecordingEventMetadata, RecordingInactivePeriod, RecordingMetadata } from '../types';
import { getSearchHighlightPieces } from '../util';
import { EventIndicator } from '../event-indicator';
import styles from './index.module.styl';
type RecordingPlayerTimelineProps = {
  replay: RecordingMetadata;
  replayEvents: RecordingEventMetadata[];
  recordingDuration: number;
  recordingProgress: number;
  recordingSkippingInactive: boolean;
  replayInactivePeriods: RecordingInactivePeriod[];
  pageloadEvents: eventWithTime[];
  searchFilter: string;
  isStitchingEnabled: boolean;
  onUpdatePlayerProgress: (timestampMs: number) => void;
  currentHoveredEvent: RecordingEventMetadata | null;
  setCurrentHoveredEvent: (event: RecordingEventMetadata | null) => void;
};
export function RecordingPlayerTimeline(props: RecordingPlayerTimelineProps) {
  var _currentProject$get$v;
  const progressBarRef = useRef<HTMLDivElement>(null);
  const currentProject = useCurrentProject();
  const isDebugIndicatorsEnabled = (_currentProject$get$v = currentProject.get().value) === null || _currentProject$get$v === void 0 ? void 0 : _currentProject$get$v.hasFeatureFlag(ProjectFeatureFlag.SessionRecordingDebugIndicators);
  function getRecordingTimelineWithInactiveSegments() {
    let filled = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : false;
    const allPeriods = [];
    let startTimeMs = props.replay.time * 1000;
    const endTimeMs = startTimeMs + props.recordingDuration;
    for (const period of props.replayInactivePeriods) {
      allPeriods.push({
        startTimeMs,
        endTimeMs: period.startTimeMs
      });
      allPeriods.push(period);
      startTimeMs = period.endTimeMs;
    }
    allPeriods.push({
      startTimeMs,
      endTimeMs
    });
    return <div className={styles.playerControlsProgressBarSegmentsContainer} data-sentry-component="getRecordingTimelineWithInactiveSegments" data-sentry-source-file="index.tsx">
        {allPeriods.map((period, index) => {
        return <div key={index} className={index % 2 === 0 ? styles.playerControlsProgressBarSegmentActive : styles.playerControlsProgressBarSegmentInactive} style={{
          width: "".concat(100 * (period.endTimeMs - period.startTimeMs) / props.recordingDuration, "%"),
          backgroundColor: filled ? props.recordingSkippingInactive ? "var(--gray4)" : "var(--blue1)" : "var(--gray2)"
        }} />;
      })}
      </div>;
  }
  function getIndicatorOffsetPercentage(timeInSeconds: number): number {
    const eventTimeOffset = Math.max(timeInSeconds - props.replay.time, 0) * 1000;
    return Math.min(100 * eventTimeOffset / props.recordingDuration, 100);
  }

  // extra indicators for rrweb events, should productionize eventually but useful for staff debugging
  function getRecordingTimelineDebugIndicators() {
    if (!isDebugIndicatorsEnabled) {
      return null;
    }
    return <div className={styles.playerControlsProgressBarDebugIndicators} data-sentry-component="getRecordingTimelineDebugIndicators" data-sentry-source-file="index.tsx">
        {props.pageloadEvents.map(event => {
        const eventOffsetPercentage = getIndicatorOffsetPercentage(event.timestamp / 1000);
        const left = "".concat(eventOffsetPercentage, "%");
        return <div className={styles.debugIndicator} key={event.timestamp} style={{
          left
        }}>
              <SvgIconReact className={styles.debugIndicatorIcon} icon='refresh' />
              <TooltipReact text='Hard Page Load' />
            </div>;
      })}
      </div>;
  }
  function getRecordingTimelineEvents() {
    let events = props.replayEvents;
    if (props.searchFilter) {
      events = props.replayEvents.filter(event => {
        return getSearchHighlightPieces(event.eventName, props.searchFilter).some(piece => piece.highlight);
      });
    }
    if (!props.isStitchingEnabled) {
      events = events.filter(event => event.hasMatchingReplayIdProperty);
    }

    // Fold events with the same timestamp into a single event so that we're not rendering indicators unnecessarily (for performance)
    // can potentially extend this to factor in visible distance between events in the timeline
    if (events.length) {
      var _events$;
      let lastEventTimestamp = (_events$ = events[0]) === null || _events$ === void 0 ? void 0 : _events$.eventTime;
      events = events.slice(1).filter(event => {
        if (event.eventTime === lastEventTimestamp) {
          return false;
        }
        lastEventTimestamp = event.eventTime;
        return true;
      });
    }
    const progressPercentage = Math.min(100 * (props.recordingProgress / props.recordingDuration), 100);
    return <div className={styles.playerControlsProgressBarEvents} data-sentry-component="getRecordingTimelineEvents" data-sentry-source-file="index.tsx">
        {events.map(event => {
        var _props$currentHovered;
        const eventOffsetPercentage = getIndicatorOffsetPercentage(event.eventTime);
        const left = "".concat(eventOffsetPercentage, "%");
        const isHovered = event.eventTime === ((_props$currentHovered = props.currentHoveredEvent) === null || _props$currentHovered === void 0 ? void 0 : _props$currentHovered.eventTime);
        return <div key={event.key} className={clsx(styles.playerControlsProgressBarEventIndicator, {
          [styles.isHovered]: isHovered
        })} style={{
          left
        }} onMouseOver={() => {
          props.setCurrentHoveredEvent(event);
        }} onMouseLeave={() => {
          props.setCurrentHoveredEvent(null);
        }}>
              <EventIndicator played={!props.recordingSkippingInactive && progressPercentage >= eventOffsetPercentage} tooltipText={event.eventName} isObserved={event.hasMatchingReplayIdProperty} isHovered={isHovered} />
            </div>;
      })}
      </div>;
  }
  if (!props.replay) {
    return null;
  }
  const cutoffPercentage = props.recordingDuration !== 0 ? 100 - Math.min(100 * (props.recordingProgress / props.recordingDuration), 100) : 100;
  return <div className={styles.playerControlsProgressBar} ref={progressBarRef} onClick={event => {
    if (progressBarRef.current) {
      const boundingBox = progressBarRef.current.getBoundingClientRect();
      const xDelta = event.clientX - boundingBox.left;
      const percentage = xDelta / boundingBox.width;
      const timeOffset = percentage * props.recordingDuration;
      props.onUpdatePlayerProgress(timeOffset);
    }
  }} data-sentry-component="RecordingPlayerTimeline" data-sentry-source-file="index.tsx">
      <div className={styles.playerControlsProgressBarEmpty}>{getRecordingTimelineWithInactiveSegments()}</div>
      <div className={styles.playerControlsProgressBarFilled} style={{
      clipPath: "inset(0% ".concat(cutoffPercentage, "% 0% 0%)")
    }}>
        {getRecordingTimelineWithInactiveSegments(true)}
      </div>
      {getRecordingTimelineEvents()}
      {getRecordingTimelineDebugIndicators()}
      <div className={styles.playerControlsProgressBarIndicator} style={props.recordingSkippingInactive ? {
      backgroundColor: "var(--gray4)",
      left: "".concat(100 - cutoffPercentage, "%")
    } : {
      left: "".concat(100 - cutoffPercentage, "%")
    }} />
    </div>;
}